<template>
	<div class="rich-number-input" :class="`${size?`rich-number-input-${size}`:''} ${draggingInProgress?'dragging':''}`">
		<input
			:id="id"
			ref="input"
			v-model="v"
			:name="name"
			type="number"
			:min="min[measuringSystem]"
			:max="max[measuringSystem]"
			:step="step[measuringSystem]"
			:class="`form-control ${size?`form-control-${size}`:''}`"
			@mousedown="inputMouseDown"
			@touchstart="inputMouseDown"
		>
		<span class="unit" :style="unitPosition"> {{ unit[measuringSystem] }} </span>
		<b-icon class="draggability-icon" icon="arrows-expand" />
	</div>
</template>

<script>
export default {
	name: 'RichNumberInput',
	props: {
		value: {
			type: Number,
			required: true,
		},
		/**
		 * The "id" attribute of the input element.
		 */
		id: {
			type: String,
			default: null,
		},
		/**
		 * The Bootstrap size class of the input element.
		 *
		 * @values sm, lg
		 */
		size: {
			type: String,
			default: null,
		},
		/**
		 * The unit text that is shown on the input element, for each measuring system.
		 *
		 * @example <rich-number-input :unit="{metric: 'm', imperial: 'ft'}" />
		 */
		unit: {
			type: Object,
			default() {
				return {
					metric: 'm',
					imperial: 'ft',
				};
			},
		},
		/**
		 * The initial value of the input element, for each measuring system.
		 *
		 * @example <rich-number-input :initial-value="{metric: 90, imperial: 30}" />
		 */
		initialValue: {
			type: Object,
			default() {
				return {
					metric: 0,
					imperial: 0,
				};
			},
		},
		/**
		 * The "min" attribute of the number input element, for each measuring system.
		 *
		 * @example <rich-number-input :min="{metric: 10, imperial: 5}" />
		 */
		min: {
			type: Object,
			default() {
				return {
					metric: 0,
					imperial: 0,
				};
			},
		},
		/**
		 * The "max" attribute of the number input element, for each measuring system.
		 *
		 * @example <rich-number-input :max="{metric: 300000, imperial: 40}" />
		 */
		max: {
			type: Object,
			default() {
				return {
					metric: 10000,
					imperial: 10000,
				};
			},
		},
		/**
		 * The "step" attribute of the number input element, for each measuring system.
		 *
		 * @example <rich-number-input :step="{metric: 10, imperial: 15}" />
		 */
		step: {
			type: Object,
			default() {
				return {
					metric: 1,
					imperial: 1,
				};
			},
		},
		/**
		 * The amount of pixels needed to drag for the input value to change by `step`, for each measuring system.
		 *
		 * For example, when the dragStepSize = 20 and step = 3, after dragging the mouse 20 pixels, the
		 * value will change by +/- 3, depending on the drag direction.
		 *
		 * @example <rich-number-input :drag-step-size="{metric: 20, imperial: 20}" />
		 */
		dragStepSize: {
			type: Object,
			default() {
				return {
					metric: 20,
					imperial: 20,
				};
			},
		},
		/**
		 * The "name" attribute of the number input element.
		 */
		name: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			measuringSystem: null,
			valueComputedStyle: null,
			dragStartYPos: null,
			dragStartInputValue: null,
			draggingInProgress: false,
		};
	},
	computed: {
		v: {
			get() {
				if (this.value === null) {
					return this.initialValue[this.measuringSystem];
				}
				return this.value;
			},
			set(v) {
				this.$emit('input', parseInt(v, 10));
			},
		},
		/**
		 * The position of the unit label inside the input element
		 */
		unitPosition() {
			// because valueComputedStyle might not be set when this is first called
			if (this.valueComputedStyle === null) {
				return {};
			}

			// NOTE: when getTextWidth() is called with a custom font, the calculated width is different
			// when this function is called the first time (presumably when the font is not loaded yet),
			// leading to a gap between value and unit that is too narrow.
			// As a workaround, use sans-serif as a font family for now as it returns always the same width
			// and looks more consistent.
			const { fontSize: valueFontSize } = this.valueComputedStyle;
			const valueFont = `${valueFontSize} sans-serif`;
			const valueTextWidth = Math.ceil(this.getTextWidth(this.value.toString(), valueFont));

			const leftPosition = `${valueTextWidth}px`;
			return { left: leftPosition };
		},
	},
	created() {
		window.addEventListener('mousemove', this.windowMouseMove);
		window.addEventListener('touchmove', this.windowMouseMove);
		window.addEventListener('mouseup', this.windowMouseUp);
		window.addEventListener('touchend', this.windowMouseUp);
	},
	mounted() {
		this.measuringSystem = this.$store.getters.getProject.units.toLowerCase();
		this.valueComputedStyle = getComputedStyle(this.$refs.input);
	},
	destroyed() {
		window.removeEventListener('mousemove', this.windowMouseMove);
		window.removeEventListener('touchmove', this.windowMouseMove);
		window.removeEventListener('mouseup', this.windowMouseUp);
		window.removeEventListener('touchend', this.windowMouseUp);
	},
	methods: {
		/**
		 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
		 *
		 * @param {String} text The text to be rendered.
		 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
		 *
		 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
		 */
		getTextWidth(text, font) {
			// re-use canvas object for better performance
			const canvas = this.getTextWidth.canvas || (this.getTextWidth.canvas = document.createElement('canvas'));
			const context = canvas.getContext('2d');
			context.font = font;
			const metrics = context.measureText(text);
			return metrics.width;
		},
		/**
		 * Gets the pageY position of either a TouchEvent or a MouseEvent
		 *
		 * @param {(TouchEvent|MouseEvent)} e
		 */
		getPageY(e) {
			if (typeof e.changedTouches !== 'undefined') {
				return e.changedTouches[0].pageY;
			}
			return e.pageY;
		},
		/**
		 * The mousedown or touchstart handler of the input object.
		 *
		 * @param {(TouchEvent|MouseEvent)} e
		 */
		inputMouseDown(e) {
			this.draggingInProgress = true;
			this.dragStartYPos = this.getPageY(e);
			this.dragStartInputValue = parseInt(this.value, 10);
		},
		/**
		 * The mousemove or touchmove handler of the window object.
		 *
		 * We need to use "window" because the event should happen when the pointer is
		 * outside of the input element.
		 *
		 * @param {(TouchEvent|MouseEvent)} e
		 */
		windowMouseMove(e) {
			if (!this.draggingInProgress) return;

			const pageY = this.getPageY(e);
			const pixelsDragged = pageY - this.dragStartYPos;
			const inputValueDifference = -Math.floor(pixelsDragged / this.dragStepSize[this.measuringSystem]);

			const newValue = this.dragStartInputValue + inputValueDifference * this.step[this.measuringSystem];
			const maxValue = this.max[this.measuringSystem];
			const minValue = this.min[this.measuringSystem];

			this.v = Math.min(Math.max(minValue, newValue), maxValue);
		},
		/**
		 * The mouseup or touchend handler of the window object.
		 */
		windowMouseUp(e) {
			if (!this.draggingInProgress) return;

			this.draggingInProgress = false;
			if (e.target !== this.$refs.input) window.getSelection().empty();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';
.rich-number-input {
	position: relative;
	input {
		padding-left: 0.4em;
		width: 100%;
	}
	input[type='number']::-webkit-inner-spin-button,
	input[type='number']::-webkit-outer-spin-button {
		opacity: 1;
		margin: (-$input-padding-y) (-$input-padding-x);
	}

	.draggability-icon {
		pointer-events: none;
		position: absolute;
		right: 1.2em;
		top: 50%;
		transform: translateY(-50%);
		opacity: 0.5;
	}

	.unit {
		pointer-events: none;
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 0.8em;
		opacity: 0.5;
	}
}

// Make unit font size the same as input font size
.rich-number-input {
	&.rich-number-input-sm .unit {
		@include font-size($input-font-size-sm);
	}
	&.rich-number-input-lg .unit {
		@include font-size($input-font-size-lg);
	}
}

.rich-number-input.dragging {
	input::selection {
		// So that text is not selected when using the dragging feature
		background: none;
	}
	.draggability-icon {
		opacity: 1;
	}
}

// The draggability icon should be closer to the edge on iOS/Android
@media (any-pointer: coarse) {
	.rich-number-input .draggability-icon {
		right: 0.4em;
	}
}

// Prevent overlapping of draggability-icon and number input spin buttons in Firefox
@-moz-document url-prefix() {
	.rich-number-input .draggability-icon {
		right: 1.7em !important;
	}
}
</style>
